<template>
  <div
    data-cy="app-icon"
    :class="['icon', iconColorClass, iconVariantClass, iconSizeClass, $attrs.class]"
  >
    <slot>
      <component
        :is="iconComponent"
        class="icon-component"
        data-sentry-unblock
      />
    </slot>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { semanticColors } from '@/constants/colors';

const props = defineProps({
  name: {
    type: String,
    default: 'info-circle',
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
  },
  color: {
    type: String,
    default: 'neutral',
    validator: (value) => semanticColors.includes(value),
  },
  variant: {
    type: String,
    default: 'basic',
    validator: (value) => ['basic', 'solid', 'outlined', 'gradient', 'box'].includes(value),
  },
});

const iconComponent = computed(() => props.name + `Icon`);
const iconVariantClass = computed(() => `icon-${props.variant}`);
const iconColorClass = computed(() => `icon-${props.color}`);
const iconSizeClass = computed(() => `icon-${props.size}`);
</script>

<style>
@reference '@/assets/main.css';

@layer components {
  .icon {
    --size: calc(var(--spacing) * 8);
    @apply relative flex items-center justify-center rounded-full size-[var(--size)] text-[var(--color-fg)];

    & .icon-component {
      @apply text-inherit;
    }

    &.icon-xs {
      --size: calc(var(--spacing) * 5);

      &.icon-gradient {
        @apply border-2;
      }

      & .icon-component {
        @apply size-3;
      }
    }

    &.icon-sm {
      --size: calc(var(--spacing) * 6);

      &.icon-gradient {
        @apply border-2;
      }

      & .icon-component {
        @apply size-4;
      }
    }

    &.icon-md {
      --size: calc(var(--spacing) * 8);

      &.icon-gradient {
        @apply border-3;
      }

      & .icon-component {
        @apply size-5;
      }
    }

    &.icon-lg {
      --size: calc(var(--spacing) * 10);

      &.icon-gradient {
        @apply border-4;
      }

      & .icon-component {
        @apply size-6;
      }
    }

    &.icon-xl {
      --size: calc(var(--spacing) * 12);

      &.icon-gradient {
        @apply border-6;
      }

      & .icon-component {
        @apply size-6;
      }
    }

    &.icon-primary {
      --color-fg: var(--color-brand-600);
      --color-bg: var(--color-brand-100);
      --border-color: var(--color-brand-50);
      --border-color-2: var(--color-brand-200);
      --border-color-solid: var(--color-brand-200);
    }

    &.icon-brand-alt {
      --color-fg: var(--color-brand-alt-600);
      --color-bg: var(--color-brand-alt-100);
      --border-color: var(--color-brand-alt-50);
      --border-color-2: var(--color-brand-alt-200);
      --border-color-solid: var(--color-brand-alt-200);
    }

    &.icon-neutral {
      --color-fg: var(--color-gray-600);
      --color-bg: var(--color-gray-100);
      --border-color: var(--color-gray-50);
      --border-color-2: var(--color-gray-200);
      --border-color-solid: var(--color-gray-200);
    }

    &.icon-success {
      --color-fg: var(--color-success-600);
      --color-bg: var(--color-success-100);
      --border-color: var(--color-success-50);
      --border-color-2: var(--color-success-200);
      --border-color-solid: var(--color-success-200);
    }

    &.icon-warning {
      --color-fg: var(--color-warning-600);
      --color-bg: var(--color-warning-100);
      --border-color: var(--color-warning-50);
      --border-color-2: var(--color-warning-200);
      --border-color-solid: var(--color-warning-200);
    }

    &.icon-error {
      --color-fg: var(--color-error-600);
      --color-bg: var(--color-error-100);
      --border-color: var(--color-error-50);
      --border-color-2: var(--color-error-200);
      --border-color-solid: var(--color-error-200);
    }
  }

  .icon {
    &.icon-basic {
      @apply size-fit text-inherit;
    }

    &.icon-box {
      --border-color: var(--color-gray-200);
      @apply border border-[var(--border-color)] rounded-lg;

      &.icon-sm {
        @apply rounded-lg;
      }
    }
  }

  .icon-box,
  .icon-gradient,
  .icon-solid,
  .icon-outlined {
    @apply min-w-[var(--size)];
  }

  .icon-solid {
    @apply border border-[var(--border-color-solid)];
  }

  .icon-gradient,
  .icon-solid {
    @apply bg-[var(--color-bg)];
  }

  .icon-gradient,
  .icon-outlined {
    @apply border-[var(--border-color)];
  }

  .icon-outlined {
    @apply border-2 border-[var(--border-color)];

    &:before {
      @apply content-[''] absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 size-[calc(var(--size)*0.8)] rounded-full border-2 border-[var(--border-color-2)];
    }

    &.icon-sm {
      @apply border-1;

      &:before {
        @apply border-1;
      }
    }

    &.icon-xl {
      @apply border-3;

      &:before {
        @apply border-3;
      }
    }
  }
}
</style>
